// Phase 5++ polish - Generate a PDF report for a parcel.
// Uses jsPDF for the document structure (selectable text, proper layout)
// and captures the current MapLibre canvas as an embedded image.

import { jsPDF } from 'jspdf';

export function exportParcelPdf({ parcel, legalText, kind, mapCanvas }) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' }); // 612 x 792 pt

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  let y = margin;

  // --- Header ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('Property Legal Description Report', margin, y);
  y += 22;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90, 90, 90);
  doc.text('Generated ' + new Date().toLocaleString(), margin, y);
  y += 18;
  doc.setTextColor(20, 20, 20);

  // --- Parcel metadata ---
  const props = parcel?.properties?.fields || parcel?.properties || {};
  const rows = [
    ['Address', val(props.address || props.saddress || props.situsaddress)],
    ['Owner', val(props.owner || props.owner1 || props.ownername)],
    ['Parcel ID', val(props.parcelnumb || props.parcel_id || props.apn)],
    ['County', val(props.scounty || props.county)],
    ['State', val(props.state2 || props.state)],
    ['Acres (approx.)', val(props.ll_gisacre || props.gisacre || props.acres)],
    ['Description kind', kind === 'aliquot' ? 'Aliquot (PLSS)' : kind === 'metes' ? 'Metes and bounds' : '\u2014'],
  ].filter(([, v]) => v && v !== '\u2014');

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Parcel', margin, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const labelWidth = 110;
  for (const [k, v] of rows) {
    doc.setTextColor(110, 110, 110);
    doc.text(k, margin, y);
    doc.setTextColor(20, 20, 20);
    const wrapped = doc.splitTextToSize(String(v), pageW - margin - margin - labelWidth);
    doc.text(wrapped, margin + labelWidth, y);
    y += 14 * wrapped.length;
  }
  y += 8;

  // --- Map snapshot ---
  if (mapCanvas) {
    try {
      const dataUrl = mapCanvas.toDataURL('image/png');
      // Fit image within remaining page width, max 250pt tall
      const imgW = pageW - margin * 2;
      const aspect = mapCanvas.height / mapCanvas.width;
      const imgH = Math.min(imgW * aspect, 260);
      // If we don't have room, push to a new page
      if (y + imgH + 40 > pageH - margin) { doc.addPage(); y = margin; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Map', margin, y);
      y += 12;
      doc.addImage(dataUrl, 'PNG', margin, y, imgW, imgH);
      y += imgH + 16;
    } catch (err) {
      // Canvas security error or similar - just skip the map
      console.warn('PDF: could not capture map canvas:', err);
    }
  }

  // --- Legal description ---
  if (legalText) {
    if (y + 40 > pageH - margin) { doc.addPage(); y = margin; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    const heading = kind === 'metes' ? 'Legal Description (Metes & Bounds)'
      : kind === 'aliquot' ? 'Legal Description (Aliquot / PLSS)'
      : 'Legal Description';
    doc.text(heading, margin, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    const lines = doc.splitTextToSize(legalText, pageW - margin * 2);
    for (const line of lines) {
      if (y > pageH - margin - 16) { doc.addPage(); y = margin; }
      doc.text(line, margin, y);
      y += 13;
    }
  }

  // --- Footer ---
  const pageCount = doc.internal.pages.length - 1;
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(
      `legal-description-generator.netlify.app  -  page ${i} of ${pageCount}`,
      margin,
      pageH - 24
    );
  }

  const filename = buildFilename(props);
  doc.save(filename);
}

function val(v) {
  if (v == null || v === '') return '\u2014';
  if (typeof v === 'number') return String(v);
  return String(v);
}

function buildFilename(props) {
  const parts = [];
  const id = props.parcelnumb || props.parcel_id || props.apn;
  if (id) parts.push(String(id).replace(/[^A-Za-z0-9._-]+/g, '_'));
  const addr = props.address || props.saddress || props.situsaddress;
  if (addr) parts.push(String(addr).slice(0, 40).replace(/[^A-Za-z0-9._-]+/g, '_'));
  return (parts.join('_') || 'legal-description') + '.pdf';
}

// Utility: find the MapLibre canvas in the DOM. Returns null if no map rendered.
export function getMapCanvas() {
  return document.querySelector('canvas.maplibregl-canvas') || null;
}
